<template>
    <div class="photo-upload-device">
        <!-- Drop-зона -->
        <div class="dropzone" :class="{ 'dropzone--active': isDragging }" @dragover.prevent="onDragOver"
            @dragleave.self="onDragLeave" @drop.prevent="onDrop" @click="triggerFileSelect">
            <input ref="fileInput" type="file" :multiple="multiple" :accept="accept" class="d-none"
                @change="onFileInputChange" />
            <i class="fas fa-cloud-upload-alt dropzone__icon"></i>
            <p class="dropzone__text">Перетащите файлы или нажмите здесь</p>
            <p class="dropzone__hint">
                <i class="fas fa-paste"></i> Ctrl+V для вставки
                &nbsp;·&nbsp;
                <i class="fas fa-image"></i> фото
                &nbsp;·&nbsp;
                <i class="fas fa-video"></i> видео
                &nbsp;·&nbsp;
            </p>
        </div>

        <!-- Шапка над сеткой: счётчик + кнопка очистки -->
        <div v-if="items.length > 0" class="files-header mt-3">
            <div class="files-header__left">
                <span class="files-header__count">
                    <i class="fas fa-images"></i>
                    {{ items.length }} {{ photosWord(items.length) }}
                </span>
                <span class="files-header__stats">
                    Принято: {{ readyPhotos.length }} · Ошибок: {{ errorCount }}
                </span>
            </div>
            <div class="files-header__right">
                <button class="btn btn-sm btn-outline-danger" type="button" @click.stop="clearAll">
                    <i class="fas fa-trash-alt"></i> Очистить всё
                </button>
            </div>
        </div>

        <div v-if="progressItems.length">
            <photo-upload-progress-bar :items="progressItems"/>
            <b-alert show variant="danger">Нельзя сворачивать страницу пока загружаете фото</b-alert>
        </div>

      
        
        <div v-if="items.length" class="photo-grid-wrapper mt-3">
            <div class="photo-grid">                
                <PhotoCard v-for="item in items" :key="item.localId" :item="item" @remove="removeById" />
            </div>
        </div>
    </div>
</template>

<script>
import PhotoCard from './PhotoCard.vue';
import PhotoUploadProgressBar from './PhotoUploadProgressBar.vue';
export default {
    components: {
        PhotoCard,
        PhotoUploadProgressBar
    },
    name: 'PhotoUploadDevice',  
    props: {
        value: {
            type: Array,
            default: () => []
        },
        accept: {
            type: String,
            default: 'image/*, video/*'
        },
        multiple: {
            type: Boolean,
            default: true
        },
        maxFiles: {
            type: Number,
            default: null
        },
        enablePaste: {
            type: Boolean,
            default: true
        }
    },
    data() {
        return {
            isDragging: false,
            items: [],
            nextLocalId: 1,
            initializedFromValue: false,
            isAlive: true,
            // При выборе файлов ОС может переключить контекст и вызвать visibilitychange.
            // Чтобы lifecycle-abort не срабатывал “ложно” в этот момент, временно снимаем WakeLock.
            isWakeLockPausedForPicking: false
        };
    },
    computed: {
        progressItems() {
            return Array.isArray(this.items)
                ? this.items.filter(item => item && ['uploading', 'ready', 'error'].includes(item.status))
                : [];
        },
        readyPhotos() {
            return this.items
                .filter(item => item.status === 'ready' && item.tempPhoto)
                .map(item => item.tempPhoto);
        },
        hasUploading() {
            return this.items.some(item => item.status === 'uploading');
        },
        errorCount() {
            return this.items.filter(item => item.status === 'error').length;
        },
        allProcessed() {
            if (!this.items.length) {
                return false;
            }
            return !this.items.some(item => item.status === 'uploading');
        },
        photosForStore() {
            return this.items.map(item => ({
                ...(item.tempPhoto || {}),
                id: item.tempPhoto && item.tempPhoto.id,
                previewUrl: item.tempPhoto && item.tempPhoto.previewUrl,
                type: item.tempPhoto && item.tempPhoto.type,
                name: item.fileMeta.name,
                localId: item.localId,
                status: item.status,
                fileMeta: item.fileMeta,
                isHeicLike: item.isHeicLike,
                iconClass: item.iconClass,
                formatLabel: item.formatLabel
            }));
        }
    },
    watch: {
        // Держим wakeLock только пока есть активные загрузки.
        hasUploading(uploading) {
            if (!this.isAlive) return;
            if (this.isWakeLockPausedForPicking) return;
            if (uploading) {
                try {
                    this.$store.dispatch('wakeLock_acquire', 'photoUpload').catch(() => {});
                } catch (e) {
                    // ignore
                }
            } else {
                try {
                    this.$store.dispatch('wakeLock_release', 'photoUpload').catch(() => {});
                } catch (e) {
                    // ignore
                }
            }
        },
        value: {
            immediate: true,
            handler(newVal) {
              
                if (this.initializedFromValue) {
                    return;
                }
                if (this.items && this.items.length > 0) {
                    this.initializedFromValue = true;
                    return;
                }
                const incoming = Array.isArray(newVal) ? newVal : [];
                this.items = incoming.map(photo => ({
                    localId: this.nextLocalId++,
                    status: 'ready',
                    tempPhoto: photo,
                    errorMessage: null,
                    fileMeta: {
                        name: photo.name || 'file',
                        size: photo.size || 0,
                        type: photo.type || 'image/*'
                    },
                    isHeicLike: false,
                    iconClass: 'fa-file-image',
                    formatLabel: this.fileFormatLabelFromName(photo.name || 'file')
                }));
                if (incoming.length) {
                    this.initializedFromValue = true;
                }
                this.emitValidity();
                // Чтобы родитель (StepEight) получил status-change и выставил allFilesProcessedForDevice
                // после восстановления списка из store / v-model, без повторной загрузки файлов.
                this.emitStatus();
            }
        }
    },
    mounted() {
        if (this.enablePaste) {
            document.addEventListener('paste', this.onPaste);
        }
        // WakeLock берём только если уже идёт загрузка (hasUploading).
        // Это предотвращает ложные lifecycle-abort на шаге с готовыми превью.
        // При появлении новых файлов wakeLock будет запрошен через watcher hasUploading.
        if (this.hasUploading) {
            try {
                this.$store.dispatch('wakeLock_acquire', 'photoUpload');
            } catch (e) {
                // ignore
            }
        }
    },
    beforeDestroy() {
        this.isAlive = false;
        if (this.enablePaste) {
            document.removeEventListener('paste', this.onPaste);
        }
        if (this.urlMap) {
            this.urlMap.forEach(url => URL.revokeObjectURL(url));
            this.urlMap.clear();
        }

        try {
            this.$store.dispatch('wakeLock_release', 'photoUpload');
        } catch (e) {
            // ignore
        }

        // Сбрасываем snapshot в lifecycle, чтобы следующий экран не унаследовал
        // устаревший статус загрузчика.
        try {
            this.$store.dispatch('lifecycle_setPhotoDeviceStatus', {
                total: 0,
                hasUploading: false,
                errorCount: 0,
                allProcessed: false,
            }).catch(() => {});
        } catch (e) {
            // ignore
        }
    },
    methods: {
        pauseWakeLockForPicking() {
            if (!this.isAlive) return;
            if (this.isWakeLockPausedForPicking) return;
            this.isWakeLockPausedForPicking = true;
            try {
                this.$store.dispatch('wakeLock_release', 'photoUpload').catch(() => {});
            } catch (e) {
                // ignore
            }
        },
        resumeWakeLockAfterPicking() {
            if (!this.isAlive) return;
            if (!this.isWakeLockPausedForPicking) return;
            this.isWakeLockPausedForPicking = false;
            try {
                this.$store.dispatch('wakeLock_acquire', 'photoUpload').catch(() => {});
            } catch (e) {
                // ignore
            }
        },
        emitValue() {
            const value = this.photosForStore.filter(
                photo => photo && photo.status === 'ready' && photo.id
            );
            this.$emit('input', value);
            this.$emit('change', value);
            this.emitValidity();
        },
        emitValidity() {
            this.$emit('validity-change', { canContinue: this.readyPhotos.length > 0 });
        },
        emitStatus() {
            const total = this.items.length;
            const readyCount = this.readyPhotos.length;
            const payload = {
                allProcessed: this.allProcessed,
                hasUploading: this.hasUploading,
                readyCount,
                errorCount: this.errorCount,
                total
            };
            this.$emit('status-change', payload);
            // Синхронизируем snapshot статуса в lifecycle store для принятия
            // решения о пропуске destructive abort.
            try {
                this.$store.dispatch('lifecycle_setPhotoDeviceStatus', {
                    total,
                    hasUploading: this.hasUploading,
                    errorCount: this.errorCount,
                    allProcessed: this.allProcessed,
                }).catch(() => {});
            } catch (e) {
                // ignore
            }
            if (this.allProcessed && total > 0 && readyCount === 0) {
                this.$emit('all-files-unavailable');
            }
        },
        onDragOver() {
            this.isDragging = true;
        },
        onDragLeave() {
            this.isDragging = false;
        },
        triggerFileSelect() {
            // Перед открытием системного file picker временно снимаем WakeLock,
            // иначе Chrome может посчитать это “уходом в hidden” и поднять lifecycle-abort.
            this.pauseWakeLockForPicking();
            if (this.$refs.fileInput) {
                this.$refs.fileInput.click();
            }
        },
        onFileInputChange(event) {
            const files = Array.from(event.target.files || []);
            event.target.value = null;
            // После получения списка файлов сразу возвращаем WakeLock.
            // Это нужно, чтобы дальнейшие async-upload’ы снова считались “важными”.
            this.resumeWakeLockAfterPicking();
            this.handleNewFiles(files);
        },
        onDrop(e) {
            this.pauseWakeLockForPicking();
            this.isDragging = false;
            const files = Array.from(e.dataTransfer.files || []);
            this.resumeWakeLockAfterPicking();
            this.handleNewFiles(files);
        },
        onPaste(e) {
            this.pauseWakeLockForPicking();
            if (!this.enablePaste) return;
            const items = e.clipboardData && e.clipboardData.items
                ? Array.from(e.clipboardData.items)
                : [];
            const files = items
                .filter(i => i.kind === 'file')
                .map(i => i.getAsFile())
                .filter(Boolean);
            this.resumeWakeLockAfterPicking();
            if (files.length) {
                this.handleNewFiles(files);
            }
        },
        handleNewFiles(newFiles) {
            const existingCount = this.items.length;
            let incoming = Array.isArray(newFiles) ? newFiles : [];
            if (!this.multiple && incoming.length) {
                incoming = [incoming[0]];
            }
            if (this.maxFiles != null) {
                const slotsLeft = Math.max(this.maxFiles - existingCount, 0);
                incoming = incoming.slice(0, slotsLeft);
            }
            incoming.forEach(file => this.enqueueFile(file));
        },
        async clearAll() {
            const tempIds = this.items
                .map(item => item.tempPhoto && item.tempPhoto.id)
                .filter(Boolean);

            if (tempIds.length) {
                try {
                    await this.$store.dispatch('shiftReport_deleteTempPhotos', tempIds);
                } catch (e) {
                    // Ошибку игнорируем, очистка temp-файлов выполнится джобом cleanupTempMedia
                }
            }

            this.items = [];
            this.emitValue();
            this.emitStatus();
        },
        async removeById(localId) {
            const item = this.items.find(el => el.localId === localId);
            const tempId = item && item.tempPhoto && item.tempPhoto.id;

            if (tempId) {
                try {
                    await this.$store.dispatch('shiftReport_deleteTempPhotos', [tempId]);
                } catch (e) {
                    // Ошибку игнорируем, очистка temp-файлов выполнится джобом cleanupTempMedia
                }
            }

            this.items = this.items.filter(el => el.localId !== localId);
            this.emitValue();
            this.emitStatus();
        },
        enqueueFile(file) {
            if (!file) return;
            const localId = this.nextLocalId++;
            const name = file.name || 'file';
            const meta = {
                name,
                size: file.size || 0,
                type: file.type || ''
            };
            const lower = String(name).toLowerCase();
            const isHeicLike = lower.endsWith('.heic') || lower.endsWith('.heif') || lower.endsWith('.heic.heif');
            const isVideo = meta.type.startsWith('video/');
            const formatLabel = this.fileFormatLabelFromName(name);
            const iconClass = isVideo ? 'fa-video' : 'fa-file-image';

            const item = {
                localId,
                status: 'uploading',
                tempPhoto: null,
                errorMessage: null,
                fileMeta: meta,
                isHeicLike,
                iconClass,
                formatLabel
            };

            this.items.push(item);
            this.uploadSingleFile(item, file);
            this.emitStatus();
        },
        async uploadSingleFile(item, file) {
            const abortTokenAtStart = this.$store.state.lifecycle && this.$store.state.lifecycle.abortToken
                ? this.$store.state.lifecycle.abortToken
                : 0;
            try {
                const response = await this.$store.dispatch('shiftReport_uploadTempPhoto', file);
                if (!this.isAlive) return;
                const currentAbortToken = this.$store.state.lifecycle && this.$store.state.lifecycle.abortToken
                    ? this.$store.state.lifecycle.abortToken
                    : 0;
                if (currentAbortToken !== abortTokenAtStart) return;
                item.tempPhoto = {
                    id: response.id,
                    previewUrl: response.previewUrl,
                    type: response.type || (item.fileMeta.type.startsWith('video/') ? 'video' : 'image'),
                    name: item.fileMeta.name
                };
                item.status = 'ready';
                item.errorMessage = null;
                this.items = this.items.map((el => {
                    el.loaded = false;
                    return el;
                }));
                this.emitValue();
                this.emitStatus();
            } catch (error) {
                if (!this.isAlive) return;
                const currentAbortToken = this.$store.state.lifecycle && this.$store.state.lifecycle.abortToken
                    ? this.$store.state.lifecycle.abortToken
                    : 0;
                if (currentAbortToken !== abortTokenAtStart) return;
                item.status = 'error';
                item.errorMessage = error && error.message ? error.message : 'Ошибка загрузки файла';
                this.emitStatus();
            }
        },
        photosWord(n) {
            const mod10 = n % 10;
            const mod100 = n % 100;
            if (mod10 === 1 && mod100 !== 11) return 'файл';
            if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return 'файла';
            return 'файлов';
        },
        fileFormatLabelFromName(name) {
            if (!name) return '';
            const lower = name.toLowerCase();
            if (lower.endsWith('.heic.heif')) return 'HEIC/HEIF';
            if (lower.endsWith('.heic')) return 'HEIC';
            if (lower.endsWith('.heif')) return 'HEIF';
            const parts = name.split('.');
            return parts.length > 1 ? parts.pop().toUpperCase() : '';
        }
    }
};
</script>

<style scoped lang="scss">
.dropzone {
    border: 2px dashed #dee2e6;
    border-radius: 12px;
    padding: 36px 20px;
    text-align: center;
    cursor: pointer;
    transition: border-color 0.2s ease, background 0.2s ease;
    background: #fafbfc;

    &:hover {
        border-color: lighten(#007bff, 20%);
        background: lighten(#e8f0fe, 4%);
    }

    &--active {
        border-color: #007bff;
        background: #e8f0fe;

        .dropzone__icon {
            color: #007bff;
            transform: scale(1.15);
        }
    }

    &__icon {
        font-size: 40px;
        color: #adb5bd;
        margin-bottom: 12px;
        display: block;
        transition: color 0.2s ease, transform 0.2s ease;
    }

    &__text {
        font-size: 15px;
        font-weight: 500;
        color: #495057;
        margin-bottom: 6px;
    }

    &__hint {
        font-size: 12px;
        color: #6c757d;
        margin: 0;

        i {
            margin-right: 2px;
        }
    }

    @media (max-width: 576px) {
        padding: 24px 16px;

        &__icon {
            font-size: 32px;
        }

        &__text {
            font-size: 14px;
        }
    }
}

.files-header {
    display: flex;
    align-items: center;
    justify-content: space-between;

    &__left {
        display: flex;
        align-items: center;
        gap: 8px;
    }

    &__count {
        font-size: 13px;
        font-weight: 600;
        color: #495057;

        i {
            color: #007bff;
            margin-right: 4px;
        }
    }

    &__stats {
        font-size: 12px;
        color: #6c757d;
    }
}

.prepare-status {
    font-size: 12px;
    color: #6c757d;
}

.photo-grid-wrapper {
    max-height: 340px;
    overflow-y: auto;
    padding-right: 2px;
    -webkit-overflow-scrolling: touch;
}

.photo-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
    gap: 10px;

    @media (max-width: 400px) {
        grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
        gap: 8px;
    }
}
</style>
